import { useUser } from '../contexts/UserContext';

export interface EntityMember {
   userId?: string;
   email?: string;
   role?: 'owner' | 'editor' | 'viewer' | 'participant' | 'organizer' | 'member' | 'attendee';
   status?: string;
}

export interface EntityData {
   id: string;
   createdBy?: string;
   organizerId?: string;
   userId?: string;
   members?: EntityMember[];
   participants?: EntityMember[];
}

/**
 * Check if current user can share photos from an entity (trip, event, or album)
 * Only owners and editors can share photos, viewers cannot
 */
export const canSharePhotos = (
   entityData: EntityData | null,
   currentUserId: string | null,
   currentUserEmail: string | null
): boolean => {
   if (!entityData || !currentUserId) {
      console.log('🔍 PhotoSharing: No entity data or current user ID', {
         hasEntityData: !!entityData,
         hasCurrentUserId: !!currentUserId,
      });
      return false;
   }

   console.log('🔍 PhotoSharing: Checking permissions for:', {
      entityId: entityData.id,
      currentUserId,
      currentUserEmail,
      createdBy: entityData.createdBy,
      organizerId: entityData.organizerId,
      userId: entityData.userId,
      entityMembers: entityData.members,
      entityParticipants: entityData.participants,
   });

   // Skip ownership checks - use only role-based permissions

   // Helper function to check if user has permission based on role
   const hasSharePermission = (role?: string) => {
      // Map different role types to permission levels
      const ownerRoles = ['owner', 'organizer', 'creator'];
      const editorRoles = ['editor', 'participant']; // In many systems, participants can share
      const viewerRoles = ['viewer', 'member']; // Members might be viewers by default

      if (!role) return false;

      return ownerRoles.includes(role) || editorRoles.includes(role);
   };

   // Check in members array (prioritize email match over user ID)
   if (entityData.members && Array.isArray(entityData.members)) {
      const userMember = entityData.members.find((member) => {
         // First try email match (more reliable for user ID mismatches)
         if (currentUserEmail && member.email === currentUserEmail) {
            return true;
         }
         // Fallback to user ID match
         return member.userId === currentUserId;
      });

      console.log('🔍 PhotoSharing: Found user member:', userMember);

      if (userMember) {
         const hasPermission = hasSharePermission(userMember.role);
         console.log('🔍 PhotoSharing: Match details:', {
            emailMatch: currentUserEmail && userMember.email === currentUserEmail,
            userIdMatch: userMember.userId === currentUserId,
            role: userMember.role,
            hasPermission,
         });
         return hasPermission;
      }
   }

   // Check in participants array (prioritize email match over user ID)
   if (entityData.participants && Array.isArray(entityData.participants)) {
      const userParticipant = entityData.participants.find((participant) => {
         // First try email match (more reliable for user ID mismatches)
         if (currentUserEmail && participant.email === currentUserEmail) {
            return true;
         }
         // Fallback to user ID match
         return participant.userId === currentUserId;
      });

      console.log('🔍 PhotoSharing: Found user participant:', userParticipant);

      if (userParticipant) {
         const hasPermission = hasSharePermission(userParticipant.role);
         console.log('🔍 PhotoSharing: Match details:', {
            emailMatch: currentUserEmail && userParticipant.email === currentUserEmail,
            userIdMatch: userParticipant.userId === currentUserId,
            role: userParticipant.role,
            hasPermission,
         });
         return hasPermission;
      }
   }

   console.log(
      '❌ PhotoSharing: User not found in members/participants or insufficient permissions'
   );
   console.log('🔍 PhotoSharing: Final check - returning false for user:', currentUserId);
   return false;
};

/**
 * React hook to check photo sharing permissions for an entity
 */
export const useCanSharePhotos = (entityData: EntityData | null): boolean => {
   const { currentUser } = useUser();

   return canSharePhotos(entityData, currentUser?.uid || null, currentUser?.email || null);
};

/**
 * Get user role in an entity
 */
export const getUserRoleInEntity = (
   entityData: EntityData | null,
   currentUserId: string | null,
   currentUserEmail: string | null
): string | null => {
   if (!entityData || !currentUserId) {
      return null;
   }

   // Skip ownership checks - use only role-based permissions

   // Check in members array (prioritize email match over user ID)
   if (entityData.members && Array.isArray(entityData.members)) {
      const userMember = entityData.members.find((member) => {
         // First try email match (more reliable for user ID mismatches)
         if (currentUserEmail && member.email === currentUserEmail) {
            return true;
         }
         // Fallback to user ID match
         return member.userId === currentUserId;
      });

      if (userMember?.role) {
         return userMember.role;
      }
   }

   // Check in participants array (prioritize email match over user ID)
   if (entityData.participants && Array.isArray(entityData.participants)) {
      const userParticipant = entityData.participants.find((participant) => {
         // First try email match (more reliable for user ID mismatches)
         if (currentUserEmail && participant.email === currentUserEmail) {
            return true;
         }
         // Fallback to user ID match
         return participant.userId === currentUserId;
      });

      if (userParticipant?.role) {
         return userParticipant.role;
      }
   }

   return null;
};

/**
 * React hook to get user role in an entity
 */
export const useUserRoleInEntity = (entityData: EntityData | null): string | null => {
   const { currentUser } = useUser();

   return getUserRoleInEntity(entityData, currentUser?.uid || null, currentUser?.email || null);
};
